import { lazy, useEffect, useMemo, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { ActionDropdown, Announce, Downloading, EditUser, SuccessDownloaded, useNVModal } from '@nv/components'
import { ListTable } from '@nv/components'
import { useConfigurationContext } from '@nv/contexts'
import { usePageNumber, usePersistedFilters, usePersistedSearch } from '@nv/hooks'
import { AdminVoterService } from '@nv/services'
import { nxConfig } from '@nv/utils'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styles from './AdminElectorsList.module.scss'
const UserFilters = lazy(() => import('@nv/lazy-loadable/lib/modals/UserFilters'))

const NUM_VOTERS_PER_PAGE = 20

export const AdminElectorsList = () => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const { votingDistrictOptions, includeKnownIdNumberField } = useConfigurationContext()

  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [numUsers, setNumUsers] = useState(0)
  const [userToEdit, setUserToEdit] = useState({ email: '' })
  const [editUserModalView, setEditUserModalView] = useState('Edit')

  const { searchInput: search, setSearchInput: setSearch } = usePersistedSearch()
  const { page, handlePageChange } = usePageNumber()

  const { NVModal: UserFiltersModal, openModal: openUserFiltersModal } = useNVModal()
  const { NVModal: SuccessDownloadedModal, openModal: openSuccessDownloadedModal } = useNVModal()
  const { NVModal: DownloadingModal, openModal: openDownloadingModal, closeModal: closeDownloadingModal } = useNVModal()
  const { NVModal: EditUserModal, openModal: openEditUserModal } = useNVModal()

  const filterOptions = [
    {
      key: 'channelFilter',
      label: t('admin.electorsList.votingChannel'),
      options: [
        { value: 'all', label: t('main.all') },
        { value: 'online', label: t('admin.electorsList.votedChannelOptions.online') },
      ],
    },
    {
      key: 'districtFilter',
      label: t('admin.electorsList.votingDistrict'),
      options: [{ value: 'All', label: t('main.all') }, ...votingDistrictOptions],
    },
    {
      key: 'statusFilter',
      label: t('admin.electorsList.status'),
      options: [
        { value: 'All', label: t('main.all'), default: true },
        { value: 'voted', label: t('admin.electorsList.votedStatus') },
      ],
    },
  ]

  const { filters, handleFilterChange }: any = usePersistedFilters({ options: filterOptions, onFilterChange: () => handlePageChange(0) })

  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: 'voterId',
        cell: (info) => info.getValue() || '',
        header: () => t('admin.electorsList.voterId'),
      },
      {
        accessorKey: 'fullName',
        cell: (info) => info.row.original?.voterInfo?.fullName || info.row.original?.fullName || '',
        header: () => t('form.labels.fullName'),
      },
      {
        accessorKey: 'dateOfBirth',
        cell: (info) => info.row.original?.voterInfo?.dateOfBirth || info.row.original?.dateOfBirth || '',
        header: () => t('form.labels.dateOfBirth'),
      },

      {
        accessorKey: 'address.fullAddress',
        cell: (info) => info.row.original?.voterInfo?.address?.fullAddress || info.row.original?.address?.fullAddress || '',
        header: () => t('form.labels.address'),
      },
      {
        accessorKey: 'address.ward',
        cell: (info) => info.row.original?.voterInfo?.address?.ward || info.row.original?.address?.ward || '',
        header: () => t('admin.electorsList.ward'),
      },
      {
        accessorKey: 'schoolSupport',
        cell: (info) => info.row.original?.voterInfo?.schoolSupport || info.row.original?.schoolSupport || '',
        header: () => t('admin.electorsList.schoolSupport'),
      },

      {
        accessorKey: 'email',
        cell: (info) => info.getValue() || '',
        header: () => t('form.labels.email'),
      },
      // {
      //   accessorKey: 'status',
      //   cell: (info) => (
      //     <span className={clsx(styles[`status-label`], styles[`status-label__${info.getValue().toLowerCase()}`])}>
      //       {t(`registration.registrationStatus.${info.getValue().toLowerCase()}`)}
      //     </span>
      //   ),
      //   header: () => t('admin.electorsList.applicationStatus'),
      // },
      {
        accessorKey: 'voted',
        cell: (info) => (info.getValue() ? t(`admin.electorsList.eligibleVoterStatusOptions.voted`) : ''),
        header: () => t('admin.electorsList.votedStatus'),
      },
    ]
    if (
      KeycloakAdminService.isSuperAdmin() ||
      KeycloakAdminService.isContactInfoRoleAdmin() ||
      KeycloakAdminService.isPollWorkerRoleAdmin()
    ) {
      cols.push({
        accessorKey: 'action',
        cell: (info) => (
          <ActionDropdown
            values={[
              { value: 'Resend access email', label: t('admin.dropdown.resendAccessEmail') },
              { value: 'Edit', label: t('admin.dropdown.edit') },
            ]}
            onCallback={(value) => {
              setUserToEdit(info.row.original)
              setEditUserModalView(value)
              openEditUserModal()
            }}
            ariaLabel={t('admin.dropdown.actionOptions', { value: info.row.original.fullName })}
          />
        ),
        header: () => t('buttons.action'),
      })
    }
    return cols
  }, [currentLanguage])

  const getUsers = async () => {
    setLoading(true)
    const resp = await AdminVoterService.fetchVoters(
      filters?.districtFilter?.value,
      filters?.channelFilter?.value,
      NUM_VOTERS_PER_PAGE,
      page * NUM_VOTERS_PER_PAGE,
      search,
      filters?.statusFilter?.value,
      'desc',
    )
    if (resp?.users) {
      setUsers(resp.users)
      setNumUsers(resp.numUsers)
    }
    setLoading(false)
  }

  useEffect(() => {
    getUsers()
  }, [page, filters, search])

  const getDowloadUrl = async () => {
    openDownloadingModal()
    const res: any = await AdminVoterService.getVoterCsv(filters.districtFilter.value, filters.channelFilter.value)
    if (res?.success) {
      const href = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.download = `voterlist`
      link.href = href
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      closeDownloadingModal()
      openSuccessDownloadedModal()
    }
  }

  return (
    <>
      <Announce title={t('pages.announcements.electorsPage')} setPageName={true} />

      <EditUserModal>
        <EditUser modalView={editUserModalView} userToEdit={userToEdit} onUpdate={() => getUsers()} />
      </EditUserModal>

      <UserFiltersModal>
        <UserFilters filterOptions={filterOptions} appliedFilters={filters} onFilterChange={handleFilterChange} />
      </UserFiltersModal>

      <DownloadingModal>
        <Downloading />
      </DownloadingModal>

      <SuccessDownloadedModal>
        <SuccessDownloaded />
      </SuccessDownloadedModal>

      <div className={styles['container']}>
        <ListTable
          data={users}
          columns={columns}
          currentPageNum={page}
          onPageChange={handlePageChange}
          dataLength={numUsers}
          loading={loading}
          showFilter={!nxConfig.useDatafix}
          setFilterModal={openUserFiltersModal}
          searchPlaceholder='admin.electorsList.search'
          searchValue={search}
          onSearch={(input) => {
            setSearch(input)
            handlePageChange(0)
          }}
          paginationLabel='admin.electorsList.electors'
          numRowsPerPage={NUM_VOTERS_PER_PAGE}
          refreshList={getUsers}
          download={KeycloakAdminService.isVoterAdminDeveloper() ? getDowloadUrl : null}
        />
      </div>
    </>
  )
}
